import { useLocale, useTranslations } from 'next-intl';
import type { Payment } from '@//app/api/commerce/payments/[id]/list';
import { DataList, DataListItem } from '@//components/DataList';
import { Link } from '@//components/Link';
import { useMarketMetadata } from '@//providers/MarketSettingsProvider';
import { formatCurrency } from '@//utils/currency';
import { WidgetDetails } from '../../../../WidgetDetails';

interface LoanDetailsProps {
  payment: Payment;
}

export const LoanDetails = ({ payment }: LoanDetailsProps) => {
  const t = useTranslations('pages.item.financeSection.loan');
  const locale = useLocale();
  const { currency } = useMarketMetadata();

  const downPayment = payment.downPayment.current;
  const vehiclePrice = payment.vehiclePrice;
  const downPaymentPct = (downPayment / vehiclePrice) * 100;

  const residualPct = payment.residual?.current ?? 0;
  const residualValue = payment.residual ? vehiclePrice * (residualPct / 100) : 0;

  const money = (amount: number, opts: any = {}) =>
    formatCurrency(amount, { locale, currency, ...opts });

  const details = [
    {
      label: t('details.downPayment'),
      value: `${money(downPayment)} (${downPaymentPct.toFixed(2)}%)`,
    },
    {
      label: t('details.residual'),
      value: `${money(residualValue)} (${residualPct.toFixed(2)}%)`,
    },
    {
      label: t('details.fixedTerm'),
      value: `${payment.duration?.current ?? '-'} ${t('fixedTerm.unit')}`,
    },
    {
      label: t('details.interest'),
      value: `${(payment.interest * 100).toFixed(2)} %`,
    },
    {
      label: t('details.effectiveInterest'),
      value: `${(payment.effectiveInterest * 100).toFixed(2)} %`,
    },
    {
      label: t('details.setupFee'),
      value: money(payment.setupFee ?? 0),
    },
    {
      label: t('details.administrationFee'),
      value: money(payment.administrationFee ?? 0, { period: 'month' }),
    },
    {
      label: t('details.totalCreditCost'),
      value: money(payment.totalCreditCost ?? 0),
    },
    {
      label: t('details.totalCost'),
      value: money(payment.totalVehicleCost ?? 0),
    },
  ];

  return (
    <WidgetDetails id="finance-loan-details">
      <DataList>
        {details.map(({ label, value }) => (
          <DataListItem key={label} label={label} value={value} />
        ))}
      </DataList>
      {payment.link && payment.financialInstitution && (
        <Link
          href={payment.link}
          title={t('moreInfoLink.title', { institution: payment.financialInstitution })}
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          {t('moreInfoLink.text', { institution: payment.financialInstitution })}
        </Link>
      )}
    </WidgetDetails>
  );
};
